import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  Platform,
  Image,
  TouchableOpacity,
  Alert,
  ScrollView,
  Modal,
  Pressable,
  Animated,
  ActivityIndicator,
} from 'react-native';
import {
  v2GetAccountTopupOptions,
  v2CreateAccountTopup,
  v2GetRates,
  v2GetMyBalances,
} from '../../../services/v2/api';
import type {
  V2Balance,
  V2AccountTopupOptions,
  V2CreateAccountTopupRequest,
  V2RateInfo,
} from '../../../types/v2';
import TabBar from '../../../components/TabBar';
import AmountCaret from '../../../components/UIComponents/AmountCaret';
import SimpleHeader from '../../../components/SimpleHeader';
import TokenIcon from '../../../components/UIComponents/TokenIcon';
import { COLORS } from '../../../theme/themeTokens';
import { COLORS_WRONG } from '../../../theme/colorsWrong';
import { formatAmount, parseAmount, numberToAmountDisplay, formatTokenAmount } from '../../../utils/amountFormat';
import { MAX_AMOUNT_DECIMALS, toRequestAmount } from '../../../utils/moneyAmount';
import { toUsdtAmount, formatUsdtAmount } from '../../../utils/usdtEquivalent';
import NumericKeypad from '../../../components/NumericKeypad';
import PINConfirmModal from '../../../components/UIComponents/PINConfirmModal';
import { usePIN } from '../../../contexts/PINContext';
import { formatDepositMeta } from '../../../utils/depositMeta';
import { useSwipeDownDismiss } from '../../../hooks/useSwipeDownDismiss';
import { WEB_SHEET, WEB_SHEET_BACKDROP, WEB_SHEET_MODAL_ANIMATION } from '../../../../web/sheet';
import { useIdempotencyKey } from '../../../hooks/useIdempotencyKey';

// Picker-sheet top inset — pins the "Current accounts" sheet top to the
// "From current account" tile top, matching OpenDeposit / Withdraw.
//   status bar (20 iOS / 28 Android)
//   + SimpleHeader (~48)
//   + contentContainer.paddingTop (16)
//   + outerCard.paddingTop + border (~17)
//   + outerCardLabel "From current account" lh 20 + mb 8 (~28)
//                                              ≈ +109
const PICKER_TOP_INSET = (Platform.OS === 'android' ? 28 : 20) + 109;

// formatBalanceLikeCard — теперь delegate к formatTokenAmount
// чтобы UI-балансы шли через ту же string-truncation что и Maximum-кнопка.
// Раньше функция использовала toFixed (округление вверх) — это давало
// рассинхрон: balance label показывал "3,06839508" а Maximum давал
// "3,06839507" для того же source-значения. Теперь оба пути truncate.
// Потолок знаков на экране живёт в formatTokenAmount: token_decimals — это
// точность контракта (18 у USDT), а не то, сколько цифр читает человек.
const formatBalanceLikeCard = (amount: number | string, decimals = 2): string =>
  formatTokenAmount(amount, decimals);

// "DD.MM.YY" — same compact form as the date range on the prototype.
const formatTermDate = (iso?: string | null): string => {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}.${mm}.${yy}`;
};

interface AccountPickerOption {
  key: string;
  symbol: string;
  networkSymbol: string;
  networkName: string;
  iconUrl: string | null;
  amount: number;
  balance: V2Balance;
}

interface TopUpDepositScreenProps {
  /** Investment account id — works for both new deposits (where the
   *  account row was created by openInvestmentAccount on confirm) and
   *  legacy investment accounts that have no v1_2_deposits row. */
  accountId: string;
  onBack: () => void;
  onNavigate: (screen: string) => void;
  onTopupSuccess?: () => void;
  repeatPrefill?: { sourceBalanceId?: string; amount?: string };
}

const TopUpDepositScreen: React.FC<TopUpDepositScreenProps> = ({
  accountId,
  onBack,
  onNavigate,
  onTopupSuccess,
  repeatPrefill,
}) => {
  const [options, setOptions] = useState<V2AccountTopupOptions | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedBalance, setSelectedBalance] = useState<V2Balance | null>(null);
  const [pickerVisible, setPickerVisible] = useState(false);
  // Swipe-to-dismiss for the source ("From") picker.
  const sourceSwipe = useSwipeDownDismiss(
    pickerVisible,
    () => setPickerVisible(false),
  );
  const [amountStr, setAmountStr] = useState('');
  const [keypadVisible, setKeypadVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pinModalVisible, setPinModalVisible] = useState(false);
  // Success modal — показывается после успешного POST /topup, заменяет
  // немедленный onTopupSuccess+onBack. Mirror'ит OpenDepositScreen success
  // modal. Закрытие (Ok / X / tap-outside) фай'рит onTopupSuccess + onBack.
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  // Live token rates — same source the dashboard / OpenDepositScreen
  // use. Drives toUsdtAmount conversions for the entered amount so the
  // "Minimum amount" check (denominated in USDT) works on any
  // source currency, not just USDT.
  const [rates, setRates] = useState<Record<string, V2RateInfo>>({});
  // Active destination investment-account — defaults to the prop but
  // switched by the "To deposit account" picker when the user owns 2+
  // investment deposits. Drives the topup-options fetch effect below.
  const [activeAccountId, setActiveAccountId] = useState<string>(accountId);
  // All user's investment balances — data source for the destination
  // picker. Fetched once on mount; closed deposits are already filtered
  // server-side by ListMyBalances.
  const [investmentBalances, setInvestmentBalances] = useState<V2Balance[]>([]);
  const [depositPickerVisible, setDepositPickerVisible] = useState(false);
  // Swipe-to-dismiss for the destination ("To deposit account") picker.
  const depositSwipe = useSwipeDownDismiss(
    depositPickerVisible,
    () => setDepositPickerVisible(false),
  );
  const { isPINSetup, isPINSkipped } = usePIN();
  // One attempt per top-up: a press after a lost answer resends it with the
  // same key, and the server does not file it twice.
  const idempotency = useIdempotencyKey<{ accountId: string; body: V2CreateAccountTopupRequest }>();

  const openDepositPicker = () => {
    // Skip the picker when there's only one deposit — the chevron
    // is hidden in that case too, so the tile reads as non-interactive.
    if (investmentBalances.length < 2) {
      return;
    }
    setDepositPickerVisible(true);
  };

  // One-shot fetch of investment balances for the picker. Independent
  // from the per-account topup-options effect below — the list shouldn't
  // re-fetch on each switch, only the selected deposit's options do.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await v2GetMyBalances();
        if (cancelled) {
          return;
        }
        // Top-up picker shows только классические depository аккаунты.
        // После rename'а (migrations 00096/00098): 'deposit' = классический
        // срочный депозит (то что раньше было 'investment'); 'investment' =
        // T-Fund-like (то что раньше было 't_fund'). T-Fund-like
        // подписочные продукты идут через другой flow.
        //
        // Дополнительно скрываем deposit'ы с available_amount=0 — это
        // означает pending close-заявку: CreateAccountClose локирует ВЕСЬ
        // investment balance через LockAmount(investBalance.Amount), так
        // что available падает до 0. Топ-ап у уходящего в закрытие депозита
        // не имеет смысла — юзеру не показываем в picker'е.
        const invest = (res?.balances ?? []).filter((b: V2Balance) => {
          if (b.account_type_code !== 'deposit') return false;
          const available = parseFloat(b.available_amount ?? b.amount ?? '0');
          return available > 0;
        });
        setInvestmentBalances(invest);
      } catch (err) {
        console.warn('[TopUpDeposit] Failed to load investment balances:', err);
        if (!cancelled) {
          setInvestmentBalances([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        // topup-options + rates in parallel — rates feed the USDT-equiv
        // comparison for the minimum-amount threshold.
        const [res, ratesRes] = await Promise.all([
          v2GetAccountTopupOptions(activeAccountId),
          v2GetRates().catch(() => ({ rates: {} as Record<string, V2RateInfo> })),
        ]);
        if (cancelled) {
          return;
        }
        setOptions(res);
        setRates(ratesRes.rates ?? {});
        // Default preselection: balance with the largest available amount
        // — currency is fixed by the account's target symbol, so the
        // "largest USDT-equiv" tiebreak doesn't apply.
        const sorted = [...(res.balances ?? [])].sort(
          (a, b) =>
            parseFloat(b.available_amount ?? b.amount ?? '0') -
            parseFloat(a.available_amount ?? a.amount ?? '0'),
        );
        const picked = (res.balances ?? []).find((balance) => balance.id === repeatPrefill?.sourceBalanceId) ?? sorted[0] ?? null;
        setSelectedBalance(picked);
        if (repeatPrefill?.amount) {
          // The token's places, not two: a repeated 0,0006 BTC must not read "0,00".
          setAmountStr(numberToAmountDisplay(
            repeatPrefill.amount,
            Math.min(picked?.token_decimals ?? 2, MAX_AMOUNT_DECIMALS),
          ));
        }
      } catch (err) {
        console.warn('[TopUpDeposit] Failed to load options:', err);
        setOptions(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeAccountId, repeatPrefill?.amount, repeatPrefill?.sourceBalanceId]);

  const pickerOptions = useMemo<AccountPickerOption[]>(() => {
    if (!options) {
      return [];
    }
    return (options.balances ?? []).map((b) => ({
      key: `${b.token_symbol}|${b.network_symbol || 'no-net'}|${b.wallet_address || b.wallet_id || b.id}`,
      symbol: b.token_symbol,
      networkSymbol: b.network_symbol || '',
      networkName: b.network_name || '',
      iconUrl: b.icon_url ?? null,
      amount: parseFloat(b.available_amount ?? b.amount ?? '0') || 0,
      balance: b,
    }));
  }, [options]);

  const currency = options?.account.symbol || options?.account.currency || 'USDT';
  // min_amount comes from v1_2_amount_settings.min_amount_top_up_deposit
  // — denominated in USDT. Compared against the entered amount's USDT
  // equivalent via toUsdtAmount + live rates; rendered as "X USDT"
  // regardless of the source-balance currency.
  const minAmount = options ? parseFloat(options.min_amount) : null;
  const feePercent = options ? parseFloat(options.fee_percent) : 0;
  const amount = parseAmount(amountStr);
  const availableBalance = selectedBalance
    ? parseFloat(selectedBalance.available_amount ?? selectedBalance.amount ?? '0') || 0
    : 0;
  const feeAmount = Number.isFinite(feePercent) ? (amount * feePercent) / 100 : 0;
  // Unknown rate → fail-open (don't block when we can't price), same
  // fallback strategy as OpenDepositScreen.
  const amountInUsdt = selectedBalance
    ? toUsdtAmount(amount, selectedBalance.token_symbol, rates)
    : null;
  const meetsMinimum =
    minAmount == null || amountInUsdt == null || amountInUsdt >= minAmount;
  const isValidAmount = amount > 0 && amount <= availableBalance && meetsMinimum;
  const canSubmit = !loading && !!selectedBalance && isValidAmount && !isSubmitting;

  const handleMaximum = () => {
    // STRING-source (минуя parseFloat) для full precision — см.
    // numberToAmountDisplay docstring про IEEE 754 артефакты на 3.06839508.
    const decimals = Math.min(selectedBalance?.token_decimals ?? 2, MAX_AMOUNT_DECIMALS);
    const balanceStr = selectedBalance?.available_amount ?? selectedBalance?.amount ?? '0';
    setAmountStr(numberToAmountDisplay(balanceStr, decimals));
  };

  const handleSelectOption = (opt: AccountPickerOption) => {
    setSelectedBalance(opt.balance);
    setPickerVisible(false);
  };

  const handleConfirm = () => {
    if (!canSubmit || !selectedBalance) {
      return;
    }
    if (!isPINSetup || isPINSkipped) {
      executeConfirm();
    } else {
      setPinModalVisible(true);
    }
  };

  const executeConfirm = async () => {
    setPinModalVisible(false);
    if (!selectedBalance) {
      return;
    }
    setIsSubmitting(true);
    try {
      // The deposit picked in the list, not the one the screen was opened from:
      // the options above were loaded for it, and so is the amount.
      const attempt = idempotency.attempt({
        accountId: activeAccountId,
        body: { source_balance_id: selectedBalance.id, amount: toRequestAmount(amountStr) },
      });
      await v2CreateAccountTopup(attempt.request.accountId, attempt.request.body, attempt.key);
      idempotency.answered();
      // Не вызываем onTopupSuccess + onBack сразу — показываем
      // success modal "Deposit application / The top-up application is
      // under consideration". onClose модалки уже выполнит callback'и.
      setSuccessModalVisible(true);
    } catch (error) {
      idempotency.failed(error);
      const errorMessage =
        error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.';
      Alert.alert('Top-up failed', errorMessage, [{ text: 'OK' }]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseSuccessModal = () => {
    setSuccessModalVisible(false);
    onTopupSuccess?.();
    onBack();
  };

  // Picker tile title — "USDT - ERC20" / "USDT" depending on network.
  const activePickerOption =
    pickerOptions.find((o) => o.balance.id === selectedBalance?.id) ?? null;
  const pickerTitle = activePickerOption
    ? activePickerOption.networkSymbol
      ? `${activePickerOption.symbol} - ${activePickerOption.networkSymbol}`
      : activePickerOption.symbol
    : currency;
  const pickerSubtitle = activePickerOption?.networkName || '';

  // Target deposit tile — unified deposit caption
  // "USDT · 6 months · 4%" on the first line, date range underneath.
  // Pulls term / apy off the V2AccountTopupTarget DTO (Open-kind
  // accounts populate them; legacy investments return NULL and the
  // helper silently drops the missing parts).
  const depositTargetSymbol = options
    ? options.account.symbol || options.account.currency
    : '';
  const depositTargetApy = options?.account.apy
    ? parseFloat(options.account.apy)
    : null;
  const depositTermLabel = options
    ? formatDepositMeta(
        depositTargetSymbol,
        options.account.term ?? null,
        Number.isFinite(depositTargetApy) ? depositTargetApy : null,
      ) || depositTargetSymbol
    : '';
  const depositRangeLabel = options && options.account.deposit_date_from && options.account.deposit_date_to
    ? `${formatTermDate(options.account.deposit_date_from)} – ${formatTermDate(options.account.deposit_date_to)}`
    : '';

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primaryNavy} />
      <SimpleHeader title="Top-up deposit" onBack={onBack} backgroundVariant="primaryNavy" />

      <View style={styles.keyboardView}>
        <ScrollView
          style={styles.content}
          contentContainerStyle={styles.contentContainer}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <Pressable onPress={() => setKeypadVisible(false)} accessible={false}>
            {/* From current account */}
            <Text style={styles.outerCardLabel}>From current account</Text>
            {loading ? (
              <View style={[styles.networkSelector, styles.networkSelectorCenter]}>
                <ActivityIndicator size="small" color={COLORS.brandOrange} />
              </View>
            ) : (
              <TouchableOpacity
                style={styles.networkSelector}
                onPress={() => setPickerVisible(true)}
                // С одним подходящим аккаунтом (либо ни одного) кликать
                // некуда — шеврон скрыт, activeOpacity=1 чтобы тап не
                // отдавал визуальный feedback. Тот же паттерн что у
                // блока "To deposit account" ниже.
                activeOpacity={pickerOptions.length > 1 ? 0.7 : 1}
                disabled={pickerOptions.length < 2}
              >
                <TokenIcon
                  symbol={selectedBalance?.token_symbol || currency}
                  iconUrl={selectedBalance?.icon_url ?? null}
                  style={styles.networkSelectorIcon}/>
                <View style={styles.networkTextContainer}>
                  <Text style={styles.networkName}>{pickerTitle}</Text>
                  {pickerSubtitle ? (
                    <Text style={styles.networkSubtitle}>{pickerSubtitle}</Text>
                  ) : null}
                </View>
                <Text style={styles.networkBalance}>
                  {formatBalanceLikeCard(selectedBalance?.available_amount ?? selectedBalance?.amount ?? '0', selectedBalance?.token_decimals)} {currency}
                </Text>
                {pickerOptions.length > 1 ? (
                  <Image
                    source={require('../../../assets/arrow_left_20.png')}
                    style={styles.chevronIcon}
                    resizeMode="contain"
                  />
                ) : null}
              </TouchableOpacity>
            )}

            {/* To deposit account — tappable picker only when the
                user owns 2+ investment deposits. With a single
                deposit the chevron is hidden and the tile is
                disabled — there's nothing to switch between. */}
            <Text style={styles.outerCardLabel}>To deposit account</Text>
            <TouchableOpacity
              style={styles.networkSelector}
              activeOpacity={investmentBalances.length > 1 ? 0.7 : 1}
              disabled={investmentBalances.length < 2}
              onPress={openDepositPicker}
            >
              <TokenIcon
                symbol={options?.account.symbol || currency}
                iconUrl={options?.account.icon_url ?? null}
                style={styles.networkSelectorIcon}/>
              <View style={styles.networkTextContainer}>
                <Text style={styles.networkName}>{depositTermLabel}</Text>
                {depositRangeLabel ? (
                  <Text style={styles.networkSubtitle}>{depositRangeLabel}</Text>
                ) : null}
              </View>
              {investmentBalances.length > 1 ? (
                <Image
                  source={require('../../../assets/arrow_left_20.png')}
                  style={styles.chevronIcon}
                  resizeMode="contain"
                />
              ) : null}
            </TouchableOpacity>

            {/* Enter amount. Amount value and currency suffix are TWO
                separate Text nodes so the blinking cursor (drawn
                between them when keypad is open) sits right after the
                number — not after "USDT". When amountStr is empty,
                value Text renders just "0" and suffix renders " USDT". */}
            <TouchableOpacity
              style={styles.inputContainer}
              activeOpacity={0.7}
              onPress={() => !isSubmitting && setKeypadVisible(true)}
              disabled={isSubmitting}
            >
              <Text style={styles.amountLabel}>Enter amount</Text>
              <Text style={[styles.amountInput, !isValidAmount && amount > 0 ? styles.amountInputInvalid : null]}>
                {amountStr || '0'}
              </Text>
              <AmountCaret active={keypadVisible} />
              <Text style={[styles.amountSuffix, !isValidAmount && amount > 0 ? styles.amountSuffixInvalid : null]}>
                {currency}
              </Text>
            </TouchableOpacity>

            {/* Min amount + Fee — flat info card matching OpenDeposit */}
            <View style={[styles.infoCard, styles.infoCardFlat]}>
              <View style={styles.infoCardRow}>
                <Text style={styles.amountLabel}>Minimum amount:</Text>
                <Text style={[styles.amountInput, styles.feeAmount, styles.feeAmountRegular]}>
                  {formatUsdtAmount(minAmount, 0)}
                </Text>
              </View>
              <View style={styles.infoCardDivider} />
              <View style={styles.infoCardRow}>
                <Text style={styles.amountLabel}>Fee, {feePercent.toFixed(0)}%</Text>
                <Text style={[styles.amountInput, styles.feeAmount, styles.feeAmountRegular]}>
                  {formatBalanceLikeCard(feeAmount, selectedBalance?.token_decimals)} {currency}
                </Text>
              </View>
            </View>

            {/* Expected processing notice */}
            <View style={styles.processingCard}>
              <Image
                source={require('../../../assets/clock-blue-steel.png')}
                style={styles.processingIcon}
                resizeMode="contain"
              />
              <Text style={styles.processingText}>
                Expected processing: up to 24 hours{'\n'}(working days)
              </Text>
            </View>

            {/* Confirm transfer */}
            <TouchableOpacity
              style={[
                styles.transferButton,
                (!canSubmit || isSubmitting) && styles.transferButtonDisabled,
              ]}
              onPress={handleConfirm}
              activeOpacity={canSubmit && !isSubmitting ? 0.8 : 1}
              disabled={!canSubmit || isSubmitting}
            >
              <Text
                style={[
                  styles.transferButtonText,
                  (!canSubmit || isSubmitting) && styles.transferButtonTextDisabled,
                ]}
              >
                {isSubmitting ? 'Processing...' : 'Confirm transfer'}
              </Text>
            </TouchableOpacity>

            {!loading && pickerOptions.length === 0 ? (
              <Text style={styles.noProductHint}>
                No current on the same network as this deposit. Open a matching current account first.
              </Text>
            ) : null}

            <View style={{ height: 40 }} />
          </Pressable>
        </ScrollView>
      </View>

      {/* Balance picker bottom-sheet — same shape as OpenDepositScreen */}
      <Modal
        visible={pickerVisible}
        transparent
        animationType={WEB_SHEET_MODAL_ANIMATION ?? 'fade'}
        statusBarTranslucent
        onRequestClose={() => setPickerVisible(false)}
      >
        <Pressable style={[styles.modalOverlay, WEB_SHEET_BACKDROP]} onPress={() => setPickerVisible(false)}>
          <Animated.View
            style={[
              styles.modalContent,
              { marginTop: PICKER_TOP_INSET },
              WEB_SHEET,
              { transform: [{ translateY: sourceSwipe.translateY }] },
            ]}
            {...sourceSwipe.panHandlers}
          >
            <Pressable onPress={(e) => e.stopPropagation()}>
              <View style={styles.modalHandle} />
              <View style={styles.modalTitleRow}>
                <Text style={styles.modalTitle}>Current accounts</Text>
                <Text style={styles.modalTitleAside}>Available balance</Text>
              </View>
              {pickerOptions.length === 0 ? (
                <Text style={[styles.modalOptionSubtitle, { paddingHorizontal: 12 }]}>
                  No current-account balances available.
                </Text>
              ) : null}
              {pickerOptions.map((opt) => {
                const isSelected = selectedBalance?.id === opt.balance.id;
                const title = opt.networkSymbol
                  ? `${opt.symbol} - ${opt.networkSymbol}`
                  : opt.symbol;
                return (
                  <TouchableOpacity
                    key={opt.key}
                    style={[styles.modalOption, isSelected && styles.modalOptionSelected]}
                    onPress={() => handleSelectOption(opt)}
                    activeOpacity={0.7}
                  >
                    <TokenIcon
                      symbol={opt.symbol}
                      iconUrl={opt.iconUrl}
                      style={styles.modalOptionIcon}/>
                    <View style={styles.modalOptionTextContainer}>
                      <Text style={styles.modalOptionName}>{title}</Text>
                      {opt.networkName ? (
                        <Text style={styles.modalOptionSubtitle}>{opt.networkName}</Text>
                      ) : null}
                    </View>
                    <Text style={styles.modalOptionAmount}>
                      {formatBalanceLikeCard(opt.amount, opt.balance.token_decimals)} {opt.symbol}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </Pressable>
          </Animated.View>
        </Pressable>
      </Modal>

      <TabBar activeTab="dashboard" onNavigate={onNavigate} />

      {/* Destination picker — opens from the "To deposit account" tile
          when the user owns 2+ investment deposits. Selecting a row
          swaps activeAccountId → reloads topup-options for the picked
          deposit. Single-investment users never see this sheet
          (openDepositPicker short-circuits). */}
      <Modal
        visible={depositPickerVisible}
        transparent
        animationType={WEB_SHEET_MODAL_ANIMATION ?? 'fade'}
        statusBarTranslucent
        onRequestClose={() => setDepositPickerVisible(false)}
      >
        <View style={[styles.modalOverlay, WEB_SHEET_BACKDROP]}>
          {/* The backdrop is a sibling of the sheet, not its parent: a
              Pressable above the list takes the touch on Android and the
              native scroll never sees it. */}
          <Pressable style={StyleSheet.absoluteFill} onPress={() => setDepositPickerVisible(false)} />
          <Animated.View
            style={[
              styles.modalContent,
              { marginTop: PICKER_TOP_INSET },
              WEB_SHEET,
              { transform: [{ translateY: depositSwipe.translateY }] },
            ]}
            {...depositSwipe.panHandlers}
          >
            <View style={styles.modalHandle} />
            <Text style={[styles.modalTitle, { marginBottom: 16 }]}>Deposit accounts</Text>
            <ScrollView showsVerticalScrollIndicator={false} {...depositSwipe.listProps}>
              {investmentBalances.map((bal) => {
                const balAccountId = bal.account_id ?? '';
                const isActive = !!balAccountId && balAccountId === activeAccountId;
                const symbol = bal.token_symbol || 'USDT';
                const fromStr = formatTermDate(bal.deposit_date_from);
                const toStr = formatTermDate(bal.deposit_date_to);
                const dateRange = fromStr && toStr ? `${fromStr} – ${toStr}` : fromStr || toStr;
                const apyFraction = bal.apy ? parseFloat(bal.apy) : null;
                // Title now uses the unified deposit caption —
                // "USDT · 3 months · 3%" — instead of the previous
                // "USDT · 3% APY". Keeps date range + amount on the
                // subtitle line so the picker still has the disambiguator.
                const titleParts = formatDepositMeta(symbol, bal.term, apyFraction);
                const subtitleParts = [
                  dateRange,
                  `${formatBalanceLikeCard(bal.amount, bal.token_decimals)} ${symbol}`,
                ].filter(Boolean);
                return (
                  <TouchableOpacity
                    key={bal.id}
                    style={[styles.modalOption, isActive && styles.modalOptionSelected]}
                    activeOpacity={0.7}
                    onPress={() => {
                      setDepositPickerVisible(false);
                      if (balAccountId && balAccountId !== activeAccountId) {
                        // Reset source picker state — the new
                        // destination has its own compatible-source
                        // list (different currency / network), so the
                        // previously-chosen source might no longer fit.
                        setSelectedBalance(null);
                        setAmountStr('');
                        setActiveAccountId(balAccountId);
                      }
                    }}
                  >
                    <TokenIcon
                      symbol={symbol}
                      iconUrl={bal.icon_url ?? null}
                      style={styles.modalOptionIcon}/>
                    <View style={styles.modalOptionTextContainer}>
                      <Text style={styles.modalOptionName}>{titleParts}</Text>
                      <Text style={styles.modalOptionSubtitle}>
                        {subtitleParts.join(' · ')}
                      </Text>
                    </View>
                  </TouchableOpacity>
                );
              })}
              </ScrollView>
          </Animated.View>
        </View>
      </Modal>

      <PINConfirmModal
        visible={pinModalVisible}
        title="Confirm top-up"
        subtitle={`Top up ${formatAmount(amountStr || '0', MAX_AMOUNT_DECIMALS)} ${currency}`}
        onSuccess={executeConfirm}
        onCancel={() => setPinModalVisible(false)}
      />

      {/* Success modal — mirror'ит OpenDepositScreen pattern. Показывается
          после успешного POST /topup. Amount подписан "+" (зачисление на
          депозит). Tap outside / X / Ok → handleCloseSuccessModal →
          onTopupSuccess + onBack. */}
      <Modal
        visible={successModalVisible}
        transparent
        animationType="fade"
        statusBarTranslucent
        onRequestClose={handleCloseSuccessModal}
      >
        <Pressable style={styles.successOverlay} onPress={handleCloseSuccessModal}>
          <Pressable style={styles.successCard} onPress={(e) => e.stopPropagation()}>
            <TouchableOpacity
              style={styles.successCloseBtn}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
              onPress={handleCloseSuccessModal}
              activeOpacity={0.6}
            >
              <Text style={styles.successCloseGlyph}>×</Text>
            </TouchableOpacity>

            <Image
              source={require('../../../assets/percentage.png')}
              style={styles.successIcon}
              resizeMode="contain"
            />
            <Text style={styles.successTitle}>Deposit application</Text>
            <Text style={styles.successSubtitle}>
              The top-up application is under consideration
            </Text>
            <View style={styles.successAmountRow}>
              <Text style={styles.successAmount}>{`+${formatAmount(amountStr || '0', MAX_AMOUNT_DECIMALS)} `}</Text>
              <Text style={styles.successCurrency}>{currency}</Text>
            </View>

            <TouchableOpacity
              style={styles.successOkButton}
              activeOpacity={0.75}
              onPress={handleCloseSuccessModal}
            >
              <Text style={styles.successOkButtonText}>Ok</Text>
            </TouchableOpacity>
          </Pressable>
        </Pressable>
      </Modal>

      <NumericKeypad
        inline
        visible={keypadVisible}
        value={amountStr}
        // The token's places, eight at most: 0,015 BTC could not be typed at two.
        onChange={(next) => setAmountStr(
          formatAmount(next, Math.min(selectedBalance?.token_decimals ?? 2, MAX_AMOUNT_DECIMALS)),
        )}
        onClose={() => setKeypadVisible(false)}
        availableValue={`${formatBalanceLikeCard(selectedBalance?.available_amount ?? selectedBalance?.amount ?? '0', selectedBalance?.token_decimals)} ${currency}`}
        onMax={handleMaximum}
        onClean={() => setAmountStr('')}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.primaryNavy,
  },
  keyboardView: {
    flex: 1,
  },
  content: {
    flex: 1,
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    overflow: 'hidden',
  },
  contentContainer: {
    // 20 dp top — uniform across Inflow / Withdraw / Close so the
    // first label ("From current account" / "From deposit account")
    // sits at the same distance from the header on every deposit
    // screen.
    paddingTop: 20,
    paddingHorizontal: 16,
    paddingBottom: 20,
  },
  outerCardLabel: {
    fontFamily: 'Inter',
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 17,
    color: COLORS.blueSteel,
    marginBottom: 8,
  },
  networkSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 60,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    marginBottom: 16,
  },
  networkSelectorIcon: {
    width: 28,
    height: 28,
    marginRight: 12,
  },
  networkTextContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  networkName: {
    fontFamily: 'Inter',
    fontWeight: '500',
    fontSize: 16,
    lineHeight: 22,
    color: COLORS.textPrimary,
  },
  networkSubtitle: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 16,
    color: COLORS.blueSteel,
    marginTop: 2,
  },
  networkBalance: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
    marginLeft: 8,
    textAlign: 'right',
  },
  chevronIcon: {
    width: 20,
    height: 20,
    marginLeft: 8,
    transform: [{ rotate: '-90deg' }],
  },
  networkSelectorCenter: {
    justifyContent: 'center',
    minHeight: 64,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 66,
    paddingHorizontal: 14,
    paddingVertical: 14,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    marginBottom: 16,
  },
  amountLabel: {
    flex: 1,
    fontFamily: 'Inter',
    fontWeight: '400',
    fontSize: 16,
    lineHeight: 20,
    color: COLORS.blueSteel,
  },
  amountInput: {
    textAlign: 'right',
    fontFamily: 'Inter',
    fontStyle: 'normal',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 20,
    color: COLORS.textPrimary,
  },
  amountInputInvalid: {
    color: COLORS_WRONG.amountInvalid,
  },
  amountSuffix: {
    fontFamily: 'Inter',
    fontStyle: 'normal',
    fontWeight: '500',
    fontSize: 16,
    lineHeight: 20,
    color: COLORS.steelNavy,
  },
  amountSuffixInvalid: {
    color: COLORS_WRONG.amountInvalid,
  },
  feeAmount: {
    flex: 1,
    color: COLORS.blueSteel,
  },
  feeAmountRegular: {
    fontWeight: '400',
  },
  infoCard: {
    backgroundColor: COLORS.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    paddingHorizontal: 14,
    paddingVertical: 6,
    marginBottom: 16,
  },
  infoCardFlat: {
    backgroundColor: COLORS.pureLight,
  },
  infoCardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 38,
  },
  infoCardDivider: {
    height: 1,
    backgroundColor: COLORS.borderMuted,
  },
  /* "Expected processing" info bar — bordered, soft tile with a leading
     clock icon and two lines of muted helper text. */
  processingCard: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    paddingVertical: 12,
    backgroundColor: COLORS.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    marginBottom: 16,
  },
  processingIcon: {
    width: 24,
    height: 24,
    marginRight: 12,
    tintColor: COLORS.blueSteel,
  },
  processingText: {
    flex: 1,
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 18,
    color: COLORS.steelNavy,
  },
  transferButton: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 10,
    gap: 12,
    height: 48,
    backgroundColor: COLORS.primaryNavy,
    borderRadius: 8,
    alignSelf: 'stretch',
  },
  transferButtonDisabled: {
    backgroundColor: COLORS_WRONG.disabled,
  },
  transferButtonText: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.white,
  },
  transferButtonTextDisabled: {
    color: COLORS.white,
  },
  noProductHint: {
    marginTop: 12,
    textAlign: 'center',
    fontFamily: 'Inter',
    fontSize: 13,
    fontWeight: '400',
    color: COLORS.blueSteel,
  },
  // Absolutely positioned, not flex: 1 — on Android the Modal's own
  // container does not hand a definite height down, so a flexed overlay
  // sizes itself to its content: the sheet's ScrollView grew as tall as
  // the list, swallowed the sheet's bottom padding, ran off the screen
  // and had nothing left to scroll. Pinning all four edges gives the
  // chain a real height and the list scrolls again.
  modalOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'transparent',
    justifyContent: 'flex-end',
  },
  modalContent: {
    flex: 1,
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 32,
  },
  modalHandle: {
    width: 40,
    height: 4,
    backgroundColor: COLORS.softGrey,
    borderRadius: 2,
    alignSelf: 'center',
    marginBottom: 16,
  },
  modalTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  modalTitle: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.primaryNavy,
    // No marginBottom — parent `modalTitleRow` owns vertical
    // centering (flexDirection: row, alignItems: center) and the
    // bottom gap to the first option below.
  },
  modalTitleAside: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.blueSteel,
  },
  modalOption: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 66,
    paddingHorizontal: 14,
    paddingVertical: 14,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: COLORS.borderMuted,
    marginBottom: 8,
    backgroundColor: COLORS.white,
  },
  modalOptionSelected: {
    borderColor: COLORS.brandOrange,
  },
  modalOptionIcon: {
    width: 28,
    height: 28,
    marginRight: 10,
  },
  modalOptionTextContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  modalOptionName: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
  },
  modalOptionSubtitle: {
    fontFamily: 'Inter',
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 16,
    color: COLORS.blueSteel,
    marginTop: 2,
  },
  modalOptionAmount: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    lineHeight: 22,
    color: COLORS.textPrimary,
    marginLeft: 8,
    textAlign: 'right',
  },
  modalOptionCheck: {
    width: 20,
    height: 20,
    marginLeft: 8,
    tintColor: COLORS.brandOrange,
  },
  // Success modal styles — copy/paste из OpenDepositScreen (см. её
  // успех-модалку после submit'а open application). Не trimm'или ничего —
  // намеренно идентичный layout для consistent'ого UX через все три экрана
  // (Open / TopUp / Outflow).
  successOverlay: {
    flex: 1,
    backgroundColor: COLORS.overlay,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  successCard: {
    width: '100%',
    backgroundColor: COLORS.white,
    borderRadius: 16,
    paddingHorizontal: 24,
    paddingTop: 48,
    paddingBottom: 24,
    alignItems: 'center',
  },
  successCloseBtn: {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  successCloseGlyph: {
    fontSize: 26,
    lineHeight: 28,
    fontWeight: '400',
    color: COLORS.steelNavy,
  },
  successIcon: {
    width: 64,
    height: 64,
    marginBottom: 28,
  },
  successTitle: {
    fontFamily: 'Inter',
    fontSize: 26,
    fontWeight: '700',
    lineHeight: 32,
    color: COLORS.textPrimary,
    textAlign: 'center',
  },
  successSubtitle: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 22,
    color: COLORS.blueSteel,
    textAlign: 'center',
    marginTop: 6,
  },
  successAmountRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    marginTop: 24,
  },
  successAmount: {
    fontFamily: 'Inter',
    fontSize: 36,
    fontWeight: '700',
    lineHeight: 42,
    color: COLORS.mint,
  },
  successCurrency: {
    fontFamily: 'Inter',
    fontSize: 22,
    fontWeight: '500',
    lineHeight: 28,
    color: COLORS.mint,
  },
  successOkButton: {
    alignSelf: 'stretch',
    marginTop: 32,
    height: 48,
    borderRadius: 8,
    backgroundColor: COLORS.primaryNavy,
    alignItems: 'center',
    justifyContent: 'center',
  },
  successOkButtonText: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.white,
  },
});

export default TopUpDepositScreen;
